import { Stack, type TableContainerProps } from "@mui/material"
import {
    MRT_ColumnDef,
    MRT_DisplayColumnIds,
    MRT_FullScreenToggleButton,
    type MRT_TableState,
} from "material-react-table"
import { type StrictOmit } from "../../../lib/lang"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import {
    LocalizedMaterialReactTable,
    type LocMrtProps,
} from "../../../modules/localization/components/LocalizedMaterialReactTable"
import { UseQueryResult } from "@tanstack/react-query"
import React, { useCallback, useMemo } from "react"
import { makeStyles } from "tss-react/mui"
import { defaultDisplayColumnDefOptions } from "../../../shared/constants/table/config"
import { ToolbarProps } from "@mui/material/Toolbar"
import { TableRowProps } from "@mui/material/TableRow"
import { TableCellProps } from "@mui/material/TableCell"
import { PaperProps } from "@mui/material/Paper"
import { tailwindPalette } from "../../../modules/theme/tailwindPalette"
import { useColumnFactory } from "../useTableColumns"
import { StandardToggleGlobalFilterButton } from "../../../shared/components/table/StandardToggleGlobalFilterButton"
import { HoverActionMenu } from "../../../shared/components/table/HoverActionMenu"
import { useTableDragAutoScroll } from "../../../shared/components/table/useTableDragAutoScroll"

const useStyles = makeStyles()(() => ({
    tableContainer: {
        maxHeight: "100%",
        borderRadius: ".75rem",
        padding: "1em 1em 3em 1em",
    },
    singleCell: {
        verticalAlign: "baseline",
    },
    tablePaper: {
        borderRadius: ".75rem",
        width: 0,
        minWidth: "100%",
    },
    tableHeadCell: {
        border: "none",
        color: tailwindPalette.slate[500],
    },
    tableHeadRow: {
        boxShadow: "none",
        background: tailwindPalette.slate[50],
    },
    topToolbar: {
        borderRadius: ".75rem",
    },
    tableFooter: {
        boxShadow: "none",
        borderRadius: "0 0 .75rem .75rem",
        "& .MuiBox-root": {
            flexDirection: "column-reverse",
            alignItems: "end",
        },
    },
}))

export type ApiQueryTableProps<TData extends Record<string, any>> = StrictOmit<
    LocMrtProps<TData>,
    "muiTableContainerProps" | "muiToolbarAlertBannerProps" | "state"
> &
    Readonly<{
        isLoading: boolean
        isFetching: boolean
        isError: boolean
        state?: StrictOmit<Partial<MRT_TableState<TData>>, "isLoading" | "showAlertBanner" | "showProgressBars">
        muiTableContainerProps?: TableContainerProps
        muiBottomToolbarProps?: ToolbarProps
        muiTablePaperProps?: PaperProps
        muiTableHeadCellProps?: TableCellProps
        muiTableHeadRowProps?: TableRowProps
        muiTopToolbarProps?: ToolbarProps
        displayColumnDefOptions?: Partial<{
            [key in MRT_DisplayColumnIds]: Partial<MRT_ColumnDef>
        }>
        enableColumnOrdering?: boolean
        enableStickyHeader?: boolean
        enableClickToCopy?: boolean
        enableDensityToggle?: boolean
        enableHiding?: boolean
        enableRowActions?: boolean
        enableGrouping?: boolean
        enableColumnDragging?: boolean
        enableExpandAll?: boolean
        positionActionsColumn?: string
        managePaginationViaUrl?: boolean
        scrollToTopOnPageChange?: boolean
    }>

export function ApiQueryTable<TData extends Record<string, any> = {}>({
    isLoading,
    isFetching,
    isError,
    state,
    muiTableContainerProps,
    muiBottomToolbarProps,
    muiTablePaperProps,
    muiTableHeadCellProps,
    muiTableHeadRowProps,
    muiTopToolbarProps,
    displayColumnDefOptions,
    enableColumnOrdering = true,
    enableStickyHeader = true,
    enableClickToCopy = false,
    enableDensityToggle = false,
    enableHiding = false,
    enableRowActions = true,
    enableGrouping = true,
    enableColumnDragging = false,
    enableExpandAll = false,
    enableRowOrdering = false,
    positionActionsColumn = "last",
    renderRowActionMenuItems,
    ...rest
}: ApiQueryTableProps<TData>) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const dragAutoScroll = useTableDragAutoScroll()

    const hoverRowActions = useMemo(() => {
        if (!renderRowActionMenuItems) return {}
        return {
            renderRowActions: ({ row, table }: { row: any; table: any }) => (
                <HoverActionMenu>
                    {(closeMenu) => renderRowActionMenuItems({ row, table, closeMenu })}
                </HoverActionMenu>
            ),
        }
    }, [renderRowActionMenuItems])

    return (
        <LocalizedMaterialReactTable
            muiTableContainerProps={{
                ...muiTableContainerProps,
                ...(enableRowOrdering ? dragAutoScroll : {}),
                className: cx(classes.tableContainer, muiTableContainerProps?.className),
            }}
            muiBottomToolbarProps={{
                ...muiBottomToolbarProps,
                className: cx(classes.tableFooter, muiBottomToolbarProps?.className),
            }}
            muiTablePaperProps={{
                ...muiTablePaperProps,
                className: cx(classes.tablePaper, muiTablePaperProps?.className),
            }}
            muiTableHeadCellProps={{
                ...muiTableHeadCellProps,
                className: cx(classes.tableHeadCell, muiTableHeadCellProps?.className),
            }}
            muiTableHeadRowProps={{
                ...muiTableHeadRowProps,
                className: cx(classes.tableHeadRow, muiTableHeadRowProps?.className),
            }}
            muiTopToolbarProps={{
                ...muiTopToolbarProps,
                className: cx(classes.topToolbar, muiTopToolbarProps?.className),
            }}
            muiToolbarAlertBannerProps={
                isError
                    ? {
                          color: "error",
                          children: l10n.getString("api-query-table-fetch-error-message"),
                      }
                    : undefined
            }
            state={{
                isLoading: isLoading,
                showProgressBars: isFetching,
                showAlertBanner: isError,
                ...state,
            }}
            positionToolbarAlertBanner={"none"}
            displayColumnDefOptions={{
                ...defaultDisplayColumnDefOptions,
                ...displayColumnDefOptions,
            }}
            enableColumnOrdering={enableColumnOrdering}
            enableStickyHeader={enableStickyHeader}
            enableClickToCopy={enableClickToCopy}
            enableDensityToggle={enableDensityToggle}
            enableHiding={enableHiding}
            enableRowActions={enableRowActions}
            enableGrouping={enableGrouping}
            enableColumnDragging={enableColumnDragging}
            enableExpandAll={enableExpandAll}
            enableRowOrdering={enableRowOrdering}
            positionActionsColumn={positionActionsColumn}
            renderToolbarInternalActions={({ table }) => (
                <Stack direction="row" paddingTop=".5rem" spacing={1}>
                    <StandardToggleGlobalFilterButton table={table} />
                    <MRT_FullScreenToggleButton table={table} />
                </Stack>
            )}
            {...hoverRowActions}
            {...rest}
        />
    )
}

type LimitedQueryResult<TData> = Pick<UseQueryResult<TData[]>, "data" | "isInitialLoading" | "isFetching" | "isError">

type RemainingProps<TData extends Record<string, any>> = StrictOmit<
    ApiQueryTableProps<TData>,
    "isLoading" | "isFetching" | "isError" | "data"
>

export function useApiQueryTable<TData extends Record<string, any>>(queryResult: LimitedQueryResult<TData>) {
    const rowData = useMemo(() => queryResult.data ?? [], [queryResult.data])
    const columnFactory = useColumnFactory<TData>()

    const apiQueryTable = useCallback(
        (props: RemainingProps<TData>) => (
            <ApiQueryTable
                isLoading={queryResult.isInitialLoading}
                isFetching={queryResult.isFetching}
                isError={queryResult.isError}
                data={rowData}
                {...props}
            />
        ),
        [queryResult.isInitialLoading, queryResult.isFetching, queryResult.isError, rowData],
    )

    return {
        columnFactory,
        ApiQueryTable: apiQueryTable,
    }
}

export function useCombinedApiQuery<TData extends Record<string, any>>(
    data: TData[] | undefined,
    queries: LimitedQueryResult<unknown>[],
) {
    const queryStates = queries
        .map((q) => q.isInitialLoading)
        .concat(queries.map((q) => q.isFetching))
        .concat(queries.map((q) => q.isError))
    const deps = [data, ...queryStates]

    const combinedQueryResult: LimitedQueryResult<TData> = useMemo(
        () => ({
            isInitialLoading: queries.map((q) => q.isInitialLoading).reduce((prev, cur) => prev || cur),
            isFetching: queries.map((q) => q.isFetching).reduce((prev, cur) => prev || cur),
            isError: queries.map((q) => q.isError).reduce((prev, cur) => prev || cur),
            data,
        }),
        deps,
    )

    return combinedQueryResult
}
